import { resolveUsgOrigin } from '@lib/http/commerceAxiosClient';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { isValidToken } from './bridgeTokenCookies';
import {
  ensureBridgeToken,
  hasUsableBridgeToken,
  publishActiveBridgeTokenSubject,
} from './ensureBridgeToken';

/**
 * Re-render signal only. Readiness itself is never remembered — it is read
 * back off the cookies each render, so an access token that expired while the
 * provider was unmounted cannot leave a stale `ready` behind.
 */
let revision = 0;
const listeners = new Set<() => void>();

const publishProvisioningOutcome = () => {
  revision += 1;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const useProvisioningRevision = () =>
  useSyncExternalStore(
    subscribe,
    () => revision,
    () => 0
  );

const useSessionIdentity = () => {
  const { data: session, status } = useSession();
  // The platform mint bearers the Unity ID JWT, not the opaque Genesis token,
  // which it rejects with a 401.
  const unityToken = session?.accessToken?.unity_token;
  const genesisUser = session?.accessToken?.genesis_user;
  const userId = typeof genesisUser === 'string' ? genesisUser : undefined;

  // Tells the request path whose token it may use, and hands it the bearer
  // the gateway wants alongside it; until the session resolves it stays
  // unknown and the gate leaves the cookies alone. Published during render,
  // not from an effect: this same render can report `isReady`, and a
  // descendant's effect — which React runs before this component's — would
  // otherwise request with the subject still unknown and get the guest token.
  // Idempotent, so a repeated render costs nothing.
  if (status !== 'loading') {
    publishActiveBridgeTokenSubject({
      userId,
      unityToken: isValidToken(unityToken) ? unityToken : undefined,
    });
  }

  return {
    sessionStatus: status,
    unityToken: isValidToken(unityToken) ? unityToken : undefined,
    userId,
  };
};

/**
 * Imperative gate: resolve the bridge token before letting a bridge request
 * leave the browser. Resolves to `undefined` when there is none, and wakes the
 * provider's org-id gate so a late success is picked up.
 */
export const useEnsureBridgeToken = () => {
  const { sessionStatus, unityToken, userId } = useSessionIdentity();
  const isSessionResolved = sessionStatus !== 'loading';

  return useCallback(async (): Promise<string | undefined> => {
    if (!isSessionResolved) {
      return undefined;
    }
    try {
      return await ensureBridgeToken({
        usgBaseUrl: resolveUsgOrigin(),
        unityToken,
        userId,
      });
    } finally {
      publishProvisioningOutcome();
    }
  }, [unityToken, userId, isSessionResolved]);
};

/**
 * Provisions the signed-in user's bridge token on mount. commerce-core's
 * OrganizationProvider issues `/commerce/bridge/v1/users/org/{orgId}` as soon
 * as it has an organizationId — before any Buy Now click — so callers must
 * withhold the org id until this reports ready.
 */
export const useBridgeTokenProvisioning = (): { isReady: boolean } => {
  const { sessionStatus, userId } = useSessionIdentity();
  const ensure = useEnsureBridgeToken();
  useProvisioningRevision();

  useEffect(() => {
    if (sessionStatus === 'loading' || sessionStatus === 'unauthenticated') {
      return;
    }
    ensure();
  }, [sessionStatus, ensure]);

  // A signed-out visitor makes no bridge call at all (no org, no cart on this
  // surface), so there is nothing to wait for and nothing worth minting.
  if (sessionStatus === 'unauthenticated') {
    return { isReady: true };
  }
  if (sessionStatus === 'loading') {
    return { isReady: false };
  }
  return { isReady: hasUsableBridgeToken(userId) };
};
